import type { CSSProperties, ReactNode } from "react";
import { Card, MetricCard, MetricStrip, type MetricChange } from "@h2j-data-team/ui";
import { cn } from "@/lib/utils";

interface KpiCellProps {
  label: string;
  value: string | number;
  subMeta?: string;
  /** Change against a previous value (the reloads' performance view). */
  change?: MetricChange;
  /** Overrides the value colour (e.g. the score cell tinted by its grade). */
  valueClassName?: string;
  /** Extra classes on the cell (e.g. dimming a deferred category). */
  className?: string;
}

/**
 * One metric of a KPI strip — also used by the compare view's delta strips and
 * the QVD compare screen. A MetricCard that is meant to sit inside a `KpiStrip`
 * (`MetricStrip`), which takes the card's frame away and puts the dividers
 * between neighbours.
 */
export function KpiCell({
  label,
  value,
  subMeta,
  change,
  valueClassName,
  className,
}: KpiCellProps) {
  return (
    <MetricCard
      className={className}
      label={label}
      value={valueClassName ? <span className={valueClassName}>{value}</span> : value}
      change={change}
      description={subMeta}
    />
  );
}

/** The framed strip that holds the KpiCells of a screen. */
export function KpiStrip({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <Card className={cn("px-5 py-4", className)}>
      {/* A tighter gap than the library's, so the six metrics of a strip share one line. */}
      <MetricStrip style={{ "--h2j-metric-strip-gap": "var(--h2j-space-6)" } as CSSProperties}>
        {children}
      </MetricStrip>
    </Card>
  );
}
